import { useEffect, useState } from 'react';
import { ERROR_PREFIXES } from 'src/common/constants';
import { useSessionManagement } from 'src/services/auth/session/hooks/useSessionManagement';
import { useMigrateToZillowState } from 'src/experiences/MigrateToZillow/hooks/useMigrateToZillowState';
import { DatadogRUMService } from 'src/services/datadog/DatadogRUMService';
import { createDatadogError } from 'src/services/datadog/utils/createDatadogError';
import { MaybeString } from 'src/common/types';
import { useSessionStoragePersistedState } from 'src/services/storage/hooks/useSessionStoragePersistedState';
import { SessionStorageKeys } from 'src/services/storage/constants';
import {
    getParsedSessionStorageItem,
    setSessionStorageItem,
    deleteSessionStorageItem,
} from 'src/services/storage/session';
import { useExperiments } from 'src/services/experiments/ContextProviders/ExperimentProvider';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';
import { areIntegrationTestsRunning } from 'src/utils/validators/areIntegrationTestsRunning';
import { SessionStatus } from '../../../constants';

export const WithAuthError = createDatadogError('WithAuth', ERROR_PREFIXES.APPLICATION_SESSION);

const MAX_LOGIN_REDIRECT_ATTEMPTS = 3;
const LOGIN_REDIRECT_WINDOW_MS = 30_000;

type LoginRedirectAttemptData = {
    count: number;
    firstAttemptAt: number;
};

const getLoginRedirectAttempts = (): LoginRedirectAttemptData | null => {
    const data = getParsedSessionStorageItem<LoginRedirectAttemptData>(SessionStorageKeys.LOGIN_REDIRECT_ATTEMPTS);
    if (!data) return null;
    if (Date.now() - data.firstAttemptAt > LOGIN_REDIRECT_WINDOW_MS) {
        deleteSessionStorageItem(SessionStorageKeys.LOGIN_REDIRECT_ATTEMPTS);
        return null;
    }
    return data;
};

const recordLoginRedirectAttempt = (): LoginRedirectAttemptData => {
    const existing = getLoginRedirectAttempts();
    const updated: LoginRedirectAttemptData = {
        count: (existing?.count ?? 0) + 1,
        firstAttemptAt: existing?.firstAttemptAt ?? Date.now(),
    };
    setSessionStorageItem(SessionStorageKeys.LOGIN_REDIRECT_ATTEMPTS, updated);
    return updated;
};

export const useDecideIfLoginIsRequired = (forceAuth: boolean, requireMfa: boolean, emailLoginHint?: MaybeString) => {
    const { isRedirectDecisionMade } = useMigrateToZillowState();
    const [hasMadeAuthenticationDecision, setHasMadeAuthenticationDecision] = useState<boolean>(forceAuth);
    const { startSession, status, session } = useSessionManagement();
    const [error, setError] = useState<unknown>(null);
    const [_, setLoginHintDisplayed] = useSessionStoragePersistedState(SessionStorageKeys.LOGIN_HINT_DISPLAYED, false);
    const [__, setPreferredLoginEmail] = useSessionStoragePersistedState(SessionStorageKeys.PREFERRED_LOGIN_EMAIL, '');
    const { experiments } = useExperiments();

    // Read synchronously to avoid race condition: useSessionStoragePersistedState
    // hydrates from storage in a useEffect, so the default (false) would be used on
    // the first render — allowing the MFA step-up effect to fire before the persisted
    // value is read, defeating the loop protection.
    const mfaStepUpAttempted = getParsedSessionStorageItem<boolean>(SessionStorageKeys.MFA_STEP_UP_ATTEMPTED) === true;

    // MFA is enabled by default for security — the flag acts as a kill switch (OFF disables MFA)
    const mfaFeatureFlag = experiments[ExperimentService.Domain.ActiveExperiment.ZHL_HOMELOANS_DASHBOARD_MFA_FF];
    const isMfaFeatureEnabled =
        mfaFeatureFlag?.treatment !== ExperimentService.Domain.FeatureFlagStatus.OFF && !areIntegrationTestsRunning();

    const loginRedirectExceeded = (getLoginRedirectAttempts()?.count ?? 0) > MAX_LOGIN_REDIRECT_ATTEMPTS;
    const shouldRedirectToLogin =
        status === SessionStatus.UNAUTHENTICATED && isRedirectDecisionMade && !loginRedirectExceeded;

    // Check if user is authenticated but needs MFA step-up.
    // Guard against infinite loops: if we already attempted MFA step-up and came back
    // without mfaVerified, don't redirect again — the step-up failed.
    const needsMfaStepUp =
        requireMfa &&
        isMfaFeatureEnabled &&
        status === SessionStatus.AUTHENTICATED &&
        session?.mfaVerified !== true &&
        isRedirectDecisionMade &&
        !mfaStepUpAttempted;

    // Clear the login redirect counter once the user is successfully authenticated
    useEffect(() => {
        if (status === SessionStatus.AUTHENTICATED) {
            deleteSessionStorageItem(SessionStorageKeys.LOGIN_REDIRECT_ATTEMPTS);
        }
    }, [status]);

    useEffect(() => {
        const maybeAuthenticate = async () => {
            try {
                // we set this regardless of if she sees the hint because we need to know if Beth activated with
                // a different email than her comms email
                if (emailLoginHint) {
                    setPreferredLoginEmail(emailLoginHint);
                }
                if (shouldRedirectToLogin) {
                    const attempts = recordLoginRedirectAttempt();
                    if (attempts.count > MAX_LOGIN_REDIRECT_ATTEMPTS) {
                        await DatadogRUMService.Client.getInstance().addError(
                            new WithAuthError('Login redirect loop detected'),
                            { error: `attempts=${attempts.count} windowMs=${LOGIN_REDIRECT_WINDOW_MS}` }
                        );
                        return;
                    }
                    if (emailLoginHint) {
                        // we use this in activate bounce to determine if she saw login  hint
                        // we have to use session storage because Beth is redirected to auth0 sign in first
                        setLoginHintDisplayed(true);
                    }
                    if (requireMfa && isMfaFeatureEnabled) {
                        setSessionStorageItem(SessionStorageKeys.MFA_STEP_UP_ATTEMPTED, true);
                    }
                    startSession(
                        'withAuth determined login is necessary',
                        { mfaRequired: requireMfa },
                        { ...(emailLoginHint && { login_hint: emailLoginHint }) }
                    );
                } else if (needsMfaStepUp) {
                    setSessionStorageItem(SessionStorageKeys.MFA_STEP_UP_ATTEMPTED, true);
                    startSession('withAuth determined MFA step-up is necessary', {
                        mfaRequired: true,
                    });
                }
            } catch (err) {
                const error = new WithAuthError(`An error occurred while logging the user in`);
                error.stack = (err as Error).stack;
                await DatadogRUMService.Client.getInstance().addError(error, {
                    error: (err as Error).message,
                });
                setError(error);
            }
            // Only clear the authentication decision flag once the redirect decision
            // has been made, so we don't prematurely allow the protected component to
            // render before the MFA step-up condition can be evaluated.
            if (isRedirectDecisionMade) {
                setHasMadeAuthenticationDecision(false);
            }
        };

        maybeAuthenticate();
    }, [
        shouldRedirectToLogin,
        needsMfaStepUp,
        startSession,
        status,
        emailLoginHint,
        setLoginHintDisplayed,
        setPreferredLoginEmail,
        requireMfa,
        isMfaFeatureEnabled,
        isRedirectDecisionMade,
    ]);

    const isClientSideDecisionPending =
        hasMadeAuthenticationDecision || status === SessionStatus.LOADING || shouldRedirectToLogin || needsMfaStepUp;

    const isAuthenticationDecisionPending = isClientSideDecisionPending;
    return { error, session, status, isAuthenticationDecisionPending };
};
